'use client';

import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SignedIn, SignedOut, SignInButton } from '@clerk/nextjs';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';
import { differenceInCalendarDays, format, isBefore, isToday } from 'date-fns';
import { isSameCalendarDay } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { Matcher } from 'react-day-picker';
import type { BookableDate, Property } from '@prisma/client';
import { BookableDateStatus, PropertyStatus } from '@/lib/enums';
import { useMediaQuery } from '@/components/ui/stepper/use-media-query';
import useSearchQueryParams from '@/hooks/useSearchQueryParams';
import { MobileCalendar } from './PropertyBookingWidget/MobileCalendar';
import { DesktopCalendar } from './PropertyBookingWidget/DesktopCalendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { AlertTriangle, ArrowRightCircleIcon, ChevronDown, ChevronUp } from 'lucide-react';
import PriceBreakdown from '@/components/elements/Bookings/PriceBreakdown';
import Price from '@/components/elements/Properties/Price';
import { fetchPropertyPrices } from '@/app/actions/serverActions';
import { PropertyPrices } from '@/app/actions/Properties/actions';
import useCurrency from '@/hooks/useCurrency';

interface PropertyWithBookableDates extends Property {
  bookableDates: Array<{
    date: Date | string;
    price: number;
    minStay?: number;
    isAvailable: boolean;
    status?: (typeof BookableDateStatus)[keyof typeof BookableDateStatus];
  }>;
  destination?: {
    label?: string;
  };
}

export default function PropertyBookingWidget({
  property,
}: {
  property: PropertyWithBookableDates;
}) {
  const { date, handleDateChange } = useSearchQueryParams();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isPriceExpanded, setIsPriceExpanded] = useState(false);
  const [bookingPrice, setBookingPrice] = useState<PropertyPrices | null>(null);
  const { currency } = useCurrency();

  const fetchPrices = async () => {
    if (!date.from || !date.to || !property.id) return;

    try {
      const prices = await fetchPropertyPrices({
        id: property.id,
        checkIn: date.from.toISOString(),
        checkOut: date.to.toISOString(),
      });

      setBookingPrice({
        nights: prices.nights,
        totalStayPrice: prices.pricingSnapshot.basePriceForStay,
        cleaningFee: prices.pricingSnapshot.cleaningFee,
        insuranceFee: prices.pricingSnapshot.insuranceFee,
        kawkbFee: prices.pricingSnapshot.kawkbFee,
        paymentFee: prices.pricingSnapshot.paymentFee,
        totalPrice: prices.pricingSnapshot.totalPrice,
      });
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };

  useEffect(() => {
    if (!date.from || !date.to) return;
    fetchPrices();
  }, [date.from, date.to, property]);

  const startMonth = useMemo(() => {
    if (property.bookableDates.length > 0) {
      const firstAvailableDate = property?.bookableDates.reduce((acc, curr) => {
        if (curr.isAvailable && new Date(curr.date).getTime() < new Date(acc.date).getTime()) {
          return curr;
        }
        return acc;
      }, property?.bookableDates[0]);
      return firstAvailableDate?.date;
    }
  }, [property?.bookableDates]);

  const minStay = useMemo(() => {
    const selectedFirstBookableDate = property?.bookableDates.find((bookableDate) => {
      {
        return (
          date.from &&
          isSameCalendarDay(new Date(bookableDate.date), date.from) &&
          bookableDate.isAvailable
        );
      }
    });
    return selectedFirstBookableDate?.minStay ?? null;
  }, [property.bookableDates, date.from]);

  const dateMatcher: Matcher = (day: Date) => {
    if (isToday(day)) return false;
    const currentDate = day.toISOString();
    const startingDate = date?.from || new Date();

    if (isBefore(new Date(currentDate), new Date())) {
      return true;
    }

    if (isBefore(new Date(currentDate), startingDate)) {
      return true;
    }

    const bookableDate = property.bookableDates?.find((d) => {
      return isSameCalendarDay(d.date, new Date(currentDate));
    });

    if (
      bookableDate?.isAvailable &&
      (bookableDate?.status === BookableDateStatus.BOOKED ||
        bookableDate?.status === BookableDateStatus.ON_HOLD)
    ) {
      return true;
    }

    return !bookableDate?.isAvailable;
  };

  const isMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Card
      className={cn(
        'w-full lg:relative p-0',
        isMobile ? 'fixed bottom-0 left-0 z-50 rounded-t-xl shadow-lg border-t' : ''
      )}
    >
      <CardHeader
        className={cn(
          'flex flex-row items-center justify-between',
          isMobile ? 'py-3 px-4' : 'py-6'
        )}
      >
        <CardTitle className="text-md lg:text-xl">
          {date && date.to && date.from
            ? `${differenceInCalendarDays(date.to, date.from)} nights in ${
                property?.destination?.label || 'this property'
              }`
            : 'Select Dates'}
        </CardTitle>
        {bookingPrice && (
          <div className="text-right">
            <Price price={bookingPrice.totalPrice} className="font-semibold" />
            <span className="text-sm text-muted-foreground block">total</span>
          </div>
        )}
      </CardHeader>
      <CardContent className={cn('grid gap-4', isMobile ? 'p-4 pt-0' : '')}>
        <div className="flex gap-2">
          {isMobile ? (
            <MobileCalendar
              isOpen={isCalendarOpen}
              onOpenChange={setIsCalendarOpen}
              date={date}
              handleDateChange={handleDateChange}
              minStay={minStay}
              dateMatcher={dateMatcher}
              bookableDates={property.bookableDates as unknown as BookableDate[]}
              startMonth={startMonth ? new Date(startMonth) : undefined}
            />
          ) : (
            <DesktopCalendar
              isOpen={isCalendarOpen}
              onOpenChange={setIsCalendarOpen}
              date={date}
              minStay={minStay}
              handleDateChange={handleDateChange}
              dateMatcher={dateMatcher}
              bookableDates={property.bookableDates as unknown as BookableDate[]}
              startMonth={startMonth ? new Date(startMonth) : undefined}
            />
          )}
        </div>

        {date && date.to && date.from && bookingPrice && isMobile && (
          <Collapsible
            open={isPriceExpanded}
            onOpenChange={setIsPriceExpanded}
            className="space-y-2"
          >
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="flex w-full justify-between items-center px-0">
                <span className="font-medium">Price details</span>
                {isPriceExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-6">
              <PriceBreakdown
                priceData={{
                  nights: bookingPrice.nights,
                  totalStayPrice: bookingPrice.totalStayPrice,
                  cleaningFee: bookingPrice.cleaningFee,
                  insuranceFee: bookingPrice.insuranceFee,
                  kawkbFee: bookingPrice.kawkbFee,
                  paymentFee: bookingPrice.paymentFee,
                  totalPrice: bookingPrice.totalPrice,
                }}
              />
              {currency != 'EGP' && (
                <div className="text-base text-muted-foreground justify-center flex items-center gap-4">
                  <AlertTriangle className="h-6 w-6" /> Estimated prices. Payment is in EGP.
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}

        <div>
          {date && date.to && date.from ? (
            <div>
              <SignedIn>
                {property?.status === PropertyStatus.PUBLISHED ? (
                  <Link
                    href={`/bookings/request?propertyId=${property.id}&checkIn=${format(date.from, 'yyyy-MM-dd')}&checkOut=${format(date.to, 'yyyy-MM-dd')}`}
                  >
                    <Button className="w-full h-12 text-base">Request to Book</Button>
                  </Link>
                ) : (
                  <Button className="w-full h-12" disabled>
                    Property is being reviewed
                  </Button>
                )}
              </SignedIn>
              <SignedOut>
                <LoginToBook
                  redirectURL={`/bookings/request?propertyId=${property.id}&checkIn=${format(date.from, 'yyyy-MM-dd')}&checkOut=${format(date.to, 'yyyy-MM-dd')}`}
                />
              </SignedOut>
            </div>
          ) : (
            <Button className="w-full h-12" disabled>
              Select dates to book
            </Button>
          )}
        </div>
      </CardContent>
      {date && date.to && date.from && bookingPrice && !isMobile && (
        <CardFooter>
          <div className="flex flex-col gap-8 w-full">
            <PriceBreakdown
              priceData={{
                nights: bookingPrice.nights,
                totalStayPrice: bookingPrice.totalStayPrice,
                cleaningFee: bookingPrice.cleaningFee,
                insuranceFee: bookingPrice.insuranceFee,
                kawkbFee: bookingPrice.kawkbFee,
                paymentFee: bookingPrice.paymentFee,
                totalPrice: bookingPrice.totalPrice,
              }}
            />
            {currency != 'EGP' && (
              <div className="text-base  text-muted-foreground justify-center flex items-center gap-4">
                <AlertTriangle className="h-6 w-6" /> Estimated prices. Payment is in EGP.
              </div>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
}

function LoginToBook({ redirectURL }: { redirectURL: string }) {
  return (
    <div className="flex items-center justify-center">
      <SignInButton signUpForceRedirectUrl={redirectURL} forceRedirectUrl={redirectURL}>
        <Button variant="default" className="w-full">
          Book Now
          <ArrowRightCircleIcon className="ml-2 h-4 w-4" />
        </Button>
      </SignInButton>
    </div>
  );
}
